import { useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";

type Lang = { code: string; label: string; flag: string };

/** Page anglaise native (rédigée, indexée) — pas de traduction automatique. */
const EN_PAGE = "/en/wedding-dj-provence";

const LANGS: Lang[] = [
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "nl", label: "Nederlands", flag: "🇳🇱" },
];

/**
 * Sélecteur de langue qui utilise le proxy translate.goog de Google.
 * Cliquer sur une langue rouvre la page courante en version traduite
 * (ex: dj-val-fr.translate.goog/contactez-nous?_x_tr_sl=fr&_x_tr_tl=en).
 * Aucun script à charger côté site, pas de barre Google moche en haut.
 */
export function LanguageSwitcher({ variant = "dark" }: { variant?: "dark" | "light" }) {
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const switchTo = (target: string) => {
    if (typeof window === "undefined") return;
    const host = window.location.hostname;
    const path = window.location.pathname + window.location.search;
    if (target === "fr") {
      // On revient au site original, sans proxy.
      // Si on est déjà sur le proxy (*.translate.goog), on reconstruit l'URL de base.
      if (host.endsWith(".translate.goog")) {
        const original = host.replace(/\.translate\.goog$/, "").replace(/-/g, ".");
        window.location.href = `https://${original}${path}`;
      } else {
        // déjà en FR
        setOpen(false);
      }
      return;
    }
    // Construit l'URL proxy: hostname avec dots → hyphens + ".translate.goog"
    // Si on est déjà sur le proxy, on change juste le paramètre _x_tr_tl
    const baseHost = host.endsWith(".translate.goog")
      ? host
      : `${host.replace(/\./g, "-")}.translate.goog`;
    window.location.href = `https://${baseHost}${path}${path.includes("?") ? "&" : "?"}_x_tr_sl=fr&_x_tr_tl=${target}&_x_tr_hl=${target}`;
  };

  const current = pathname.startsWith("/en") ? "EN" : "FR";

  const isLight = variant === "light";
  const buttonCls = isLight
    ? "text-white/85 hover:text-white"
    : "text-foreground/85 hover:text-foreground";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Choisir la langue / Choose language"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] transition-colors ${buttonCls}`}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4" aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
        </svg>
        <span className="hidden sm:inline">{current}</span>
        <span aria-hidden className="text-[8px]">▾</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-3 min-w-[180px] border border-foreground/10 bg-background/98 backdrop-blur-md shadow-xl"
        >
          <ul className="py-1 border-b border-foreground/10">
            <li>
              <a
                href="/"
                hrefLang="fr-FR"
                role="menuitem"
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground/85 hover:bg-foreground/5 hover:text-foreground transition-colors"
              >
                <span className="text-base leading-none" aria-hidden>🇫🇷</span>
                <span>Français</span>
              </a>
            </li>
            <li>
              <a
                href={EN_PAGE}
                hrefLang="en"
                role="menuitem"
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground/85 hover:bg-foreground/5 hover:text-foreground transition-colors"
              >
                <span className="text-base leading-none" aria-hidden>🇬🇧</span>
                <span>English</span>
              </a>
            </li>
          </ul>
          <p className="px-4 pt-3 pb-2 text-[9px] uppercase tracking-[0.3em] text-muted-foreground border-b border-foreground/10">
            Traduire le site
          </p>
          <ul className="py-1">
            {LANGS.map((l) => (
              <li key={l.code}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => switchTo(l.code)}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground/85 hover:bg-foreground/5 hover:text-foreground transition-colors"
                >
                  <span className="text-base leading-none" aria-hidden>{l.flag}</span>
                  <span>{l.label}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="px-4 py-2 text-[10px] text-muted-foreground border-t border-foreground/10 leading-snug">
            Traduction automatique par Google.
          </p>
        </div>
      )}
    </div>
  );
}
